interface UBALogEntry {
  timestamp: string;
  eventName: string;
  userAgent?: string;
  deviceType?: string;
  isMobile?: boolean;
  [key: string]: unknown;
}

const FLUSH_INTERVAL_MS = 5000; // 5 seconds
const DEFAULT_MAX_BUFFER_SIZE = 1; // flush after 1 event
const API_ENDPOINT = `${process.env.NEXT_PUBLIC_UBA_AUDIT_URL}/api/uba-trace-audit`;

import {jobfeedService} from '@jobseeker/common-core/services';

interface ubaLoggingCriteria {
  enableUBALogging: boolean;
  maxBufferSize?: number;
  eventNames?: string[];
  pageNames?: string[];
  state?: 'all' | 'loggedIn' | 'loggedOut';
}

class UBAEventLogger {
  private buffer: UBALogEntry[] = [];
  private flushTimer: ReturnType<typeof setInterval> | null = null;
  private initialized = false;
  private isEnabled: boolean | null = null;
  private criteria: ubaLoggingCriteria | null = null;
  private pendingInit: Promise<boolean> | null = null;

  private async checkFeatureFlag(): Promise<boolean> {
    if (this.isEnabled !== null) return this.isEnabled;

    // Dedupe concurrent checks
    if (this.pendingInit) return this.pendingInit;

    this.pendingInit = (async () => {
      try {
        const response = await jobfeedService.getFeatureFlags();
        const criteria = response?.ubaLoggingCriteria as ubaLoggingCriteria;
        if (criteria) {
          this.criteria = criteria;
          this.isEnabled = !!criteria.enableUBALogging;
        } else {
          // Fallback for old structure or if property missing
          this.isEnabled = !!response?.enableUBALogging;
        }
      } catch (error) {
        // Fail safe: disable logging on error
        this.isEnabled = false;
      } finally {
        this.pendingInit = null;
      }
      return this.isEnabled;
    })();

    return this.pendingInit;
  }

  private init(): void {
    if (this.initialized || typeof window === 'undefined') return;
    this.initialized = true;

    // Periodic flush
    this.flushTimer = setInterval(() => this.flush(), FLUSH_INTERVAL_MS);

    // Flush on page unload using sendBeacon
    window.addEventListener('beforeunload', () => {
      this.flushWithBeacon();
    });

    // Also flush on visibility change (tab switch)
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        this.flushWithBeacon();
      }
    });
  }

  /**
   * Public API: Logs event if feature flag allows
   */
  logEvent(eventName: string, keyNamesObject: Record<string, unknown>): void {
    if (typeof window === 'undefined') return;

    // If explicitly disabled, ignore
    if (this.isEnabled === false) return;

    // If unknown, check flag then log
    if (this.isEnabled === null) {
      this.checkFeatureFlag().then(enabled => {
        if (enabled && this.shouldLogEvent(eventName, keyNamesObject)) {
          this.queueEvent(eventName, keyNamesObject);
        }
      });
      return;
    }

    // If enabled, check criteria then log
    if (this.shouldLogEvent(eventName, keyNamesObject)) {
      this.queueEvent(eventName, keyNamesObject);
    }
  }

  private shouldLogEvent(
    eventName: string,
    keyNamesObject: Record<string, unknown>
  ): boolean {
    if (!this.criteria) return true;

    const {eventNames, pageNames, state} = this.criteria;

    // Filter by Event Name
    if (
      eventNames &&
      eventNames.length > 0 &&
      !eventNames.includes(eventName)
    ) {
      return false;
    }

    // Filter by Page Name
    if (pageNames && pageNames.length > 0) {
      const currentPage = keyNamesObject['pageName'] as string;
      if (!currentPage || !pageNames.includes(currentPage)) {
        return false;
      }
    }

    // Filter by Auth State
    if (state && state !== 'all') {
      const sessionUsername = keyNamesObject['sessionUsername'];
      const isLoggedIn =
        typeof sessionUsername === 'string' &&
        sessionUsername.length > 0 &&
        sessionUsername !== '-1';

      if (state === 'loggedIn' && !isLoggedIn) return false;
      if (state === 'loggedOut' && isLoggedIn) return false;
    }

    return true;
  }

  /**
   * Internal: Buffer a UBA event for batched sending
   */
  private queueEvent(
    eventName: string,
    keyNamesObject: Record<string, unknown>
  ): void {
    this.init();

    const userAgent = navigator.userAgent;
    const deviceType = this.getDeviceOs(userAgent);
    const isMobile = /(iPad|iPhone|Android|Mobile)/i.test(userAgent) || false;

    const entry: UBALogEntry = {
      timestamp: new Date().toISOString(),
      eventName,
      appId: process.env.NEXT_PUBLIC_UBA_APP_ID,
      url: window.location.href,
      userAgent,
      deviceType,
      isMobile,
      ...keyNamesObject,
    };

    this.buffer.push(entry);

    const maxBufferSize =
      this.criteria?.maxBufferSize || DEFAULT_MAX_BUFFER_SIZE;

    if (this.buffer.length >= maxBufferSize) {
      this.flush();
    }
  }

  /**
   * Flush buffered events to the server via fetch.
   */
  private async flush(): Promise<void> {
    if (this.buffer.length === 0) return;

    const events = [...this.buffer];
    this.buffer = [];

    try {
      await fetch(API_ENDPOINT, {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({events}),
      });
    } catch (error) {
      // On failure, push events back to buffer for retry
      this.buffer.unshift(...events);
      // Cap buffer to prevent unbounded growth on persistent failures
      const maxBufferSize =
        this.criteria?.maxBufferSize || DEFAULT_MAX_BUFFER_SIZE;
      if (this.buffer.length > maxBufferSize * 5) {
        this.buffer = this.buffer.slice(-maxBufferSize * 5);
      }
    }
  }

  /**
   * Flush using sendBeacon for reliable delivery on page unload.
   * sendBeacon is fire-and-forget and survives page navigation.
   */
  private flushWithBeacon(): void {
    if (this.buffer.length === 0) return;

    const events = [...this.buffer];
    this.buffer = [];

    try {
      const blob = new Blob([JSON.stringify({events})], {
        type: 'application/json',
      });
      navigator.sendBeacon(API_ENDPOINT, blob);
    } catch {
      // Last resort — data loss is acceptable on unload
    }
  }

  /**
   * Clean up timers (for testing or hot-reload scenarios).
   */
  destroy(): void {
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
      this.flushTimer = null;
    }
    this.flush();
    this.initialized = false;
  }
  /**
   * Helper to determine device OS from user agent
   */
  private getDeviceOs(userAgent: string): string {
    const isMobile =
      /(iPad|iPhone|Android|webOS|iPod|BlackBerry|IEMobile|Opera Mini|Mobile)/i.test(
        userAgent
      ) || false;

    if (isMobile) {
      if (/android/i.test(userAgent)) {
        return 'android';
      }

      if (/iphone|ipad|ipod|iPhone|ios/.test(userAgent)) {
        return 'ios';
      }
    }

    return 'web';
  }
}

// Singleton instance
const ubaEventLogger = new UBAEventLogger();

export default ubaEventLogger;
